import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { DesignProvider, useDesignContext } from './hooks/useDesignContext';
import './index.css';
import GlobalLoader from './components/GlobalLoader';
import { buildApiUrl, authHeaders } from './config/api';
import { ToastHost } from './components/Toast';
import LandingPage from './components/LandingPage';
import { decryptSharedDesignFromUrlParam } from './utils/shareLinkCrypto';

// Re-exporting is useful for typing
export type { DesignProvider };

// Lazy load admin, customer apps, and promo page to reduce initial bundle size
const AdminApp = React.lazy(() => import('./admin/AdminApp'));
const CustomerDashboardApp = React.lazy(() => import('./customer/CustomerDashboardApp'));
const PromoPricePage = React.lazy(() => import('./pages/PromoPricePage'));
const LspQdUserGuidePage = React.lazy(() => import('./pages/LspQdUserGuidePage'));
const EsqUserGuidePage = React.lazy(() => import('./pages/EsqUserGuidePage'));


// Global fetch wrapper to auto-logout on 401 from any request
const __originalFetch = window.fetch.bind(window);
window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const res = await __originalFetch(input as any, init as any);
  try {
    if (res.status === 401) {
      const url = typeof input === 'string' ? input : (input as Request).url;
      // Avoid forced logout on background token checks that can occasionally fail transiently.
      // Real protected API calls will still trigger logout.
      if (url && url.includes('/api/admin/auth/verify')) {
        return res;
      }
      if (url && url.includes('/api/admin')) {
        try { localStorage.removeItem('admin_token'); } catch { }
        try { window.dispatchEvent(new Event('admin:logout')); } catch { }
      } else {
        try { localStorage.removeItem('auth_token'); } catch { }
        try { sessionStorage.removeItem('auth_token'); } catch { }
        try { localStorage.removeItem('auth_user'); } catch { }
        try { sessionStorage.removeItem('auth_user'); } catch { }
        try { window.dispatchEvent(new Event('auth:logout')); } catch { }
      }
    }
  } catch { }
  return res;
};

// Lightweight auth watchdog: verify tokens periodically and on focus/visibility
function startAuthWatchdog() {
  const verifyCustomer = async () => {
    try {
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      if (!token) return;
      const res = await fetch(buildApiUrl('/api/profile'), { headers: { ...authHeaders() } });
      if (res.status === 401) {
        // Clear local state immediately if server says unauthorized
        localStorage.removeItem('auth_token');
        sessionStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        sessionStorage.removeItem('auth_user');
        window.dispatchEvent(new Event('auth:logout'));
      } else if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data && data.user) {
          try {
            if (localStorage.getItem('auth_token')) localStorage.setItem('auth_user', JSON.stringify(data.user));
            if (sessionStorage.getItem('auth_token')) sessionStorage.setItem('auth_user', JSON.stringify(data.user));
            window.dispatchEvent(new CustomEvent('auth:user_updated', { detail: data.user }));
          } catch { }
        }
      }
    } catch { }
  };

  const verifyAdmin = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) return;
      const res = await fetch(buildApiUrl('/api/admin/auth/verify'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        localStorage.removeItem('admin_token');
        window.dispatchEvent(new Event('admin:logout'));
      }
    } catch { }
  };

  const runVerify = () => {
    const pathname = window.location.pathname;
    if (pathname.startsWith('/admin')) {
      void verifyAdmin();
    } else {
      void verifyCustomer();
    }
  };

  // Periodic check only — no focus/visibilitychange triggers to avoid
  // firing on every tab switch.
  const intervalId = window.setInterval(runVerify, 5 * 60_000); // every 5 min is sufficient

  // Initial verify shortly after boot
  setTimeout(runVerify, 1000);

  // Return cleanup in case needed in the future
  return () => {
    clearInterval(intervalId);
  };
}

startAuthWatchdog();

// Heartbeat: keep user's presence fresh (pings every 60s while tab is visible)
function startHeartbeat() {
  const getTokens = () => {
    try {
      return {
        auth: localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token'),
        admin: localStorage.getItem('admin_token')
      };
    } catch { return {}; }
  };

  const ping = async () => {
    try {
      // If tab is hidden, do NOT send heartbeats. This reduces "ghost" online users.
      if (document.visibilityState !== 'visible') return;

      const { auth } = getTokens();
      if (!auth) return;

      // Skip heartbeat if we are on an admin page (Admin has its own refresh logic)
      if (window.location.pathname.startsWith('/admin')) return;

      await fetch(buildApiUrl('/api/ping'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        keepalive: true,
      });
    } catch { }
  };

  const signOff = () => {
    const { auth } = getTokens();
    if (!auth) return;

    // navigator.sendBeacon is highly reliable for tab closure/navigation events
    // We pass the token in the query string to bypass header restrictions in sendBeacon
    const url = buildApiUrl(`/api/sign-off?token=${encodeURIComponent(auth)}`);
    navigator.sendBeacon(url);
  };

  const onUnload = () => signOff();

  // Only the interval ping — no focus/visibilitychange listeners so switching
  // tabs does not trigger extra requests.
  window.addEventListener('pagehide', onUnload);

  const intervalId = window.setInterval(ping, 60_000);
  setTimeout(ping, 2000);

  return () => {
    window.removeEventListener('pagehide', onUnload);
    clearInterval(intervalId);
  };
}

startHeartbeat();

// Cross-tab propagation: respond to storage changes
window.addEventListener('storage', (e: StorageEvent) => {
  try {
    if (e.key === 'auth_token') {
      const hasToken = Boolean(localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token'));
      if (!hasToken) {
        window.dispatchEvent(new Event('auth:logout'));
      }
    }
    if (e.key === 'logout_signal') {
      try { localStorage.removeItem('auth_token'); } catch { }
      try { sessionStorage.removeItem('auth_token'); } catch { }
      try { localStorage.removeItem('auth_user'); } catch { }
      try { sessionStorage.removeItem('auth_user'); } catch { }
      window.dispatchEvent(new Event('auth:logout'));
    }
    if (e.key === 'admin_token') {
      const hasAdmin = Boolean(localStorage.getItem('admin_token'));
      if (!hasAdmin) {
        window.dispatchEvent(new Event('admin:logout'));
      }
    }
  } catch { }
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const pathname = window.location.pathname;
const pathnameDecoded = decodeURIComponent(pathname).toLowerCase();
const isAdminRoute = pathname.startsWith('/admin');
const isDesignRoute = pathname.startsWith('/design');
const isPromoRoute = pathname.startsWith('/promo');
const isLspQdGuideRoute =
  pathnameDecoded.startsWith('/lsp&qd-user-guide') ||
  pathnameDecoded.startsWith('/lsp-qd-user-guide') ||
  pathnameDecoded.startsWith('/lsp_qd_user_guide') ||
  pathnameDecoded.startsWith('/lsp-qd-guide');
const isEsqGuideRoute =
  pathnameDecoded.startsWith('/esq-user-guide') ||
  pathnameDecoded.startsWith('/esq_user_guide') ||
  pathnameDecoded.startsWith('/esq-guide');
const isCategoriesRoute = pathname.startsWith('/categories') || pathname.startsWith('/catagories');

const DesignModeWrapper: React.FC = () => {
  const { setAuthedUser } = useDesignContext();

  const getSharedParams = () => {
    try {
      const u = new URL(window.location.href);
      return {
        token: u.searchParams.get('d'),
        sid: u.searchParams.get('sid'),
        loadId: u.searchParams.get('loadDesignId'),
        ssoToken: u.searchParams.get('sso_token')
      };
    } catch {
      return { token: null as string | null, sid: null as string | null, loadId: null as string | null, ssoToken: null as string | null };
    }
  };

  const inferModeFromSharedPayload = (raw: any): 'LSP/QD' | 'ESQ' => {
    const design = (raw && (raw.design || raw.data || raw)) || {};
    // Check top-level designMode first
    const topMode = String((design as any)?.designMode || (design as any)?.mode || '').toUpperCase();
    if (topMode === 'ESQ') return 'ESQ';
    if (topMode === 'LSP/QD' || topMode === 'LSP' || topMode === 'LSP-QD') return 'LSP/QD';

    const selectedEsq = Number((design as any)?.selectedEsqModalId || 0);
    if (selectedEsq > 0) return 'ESQ';

    const elements = Array.isArray((design as any)?.elements) ? (design as any).elements : [];
    for (const el of elements) {
      const p = (el && el.properties) || {};
      const m = String(p?.designMode || '').toUpperCase();
      if (m === 'ESQ') return 'ESQ';
      if (Number(p?.esqModalId || 0) > 0) return 'ESQ';
      if (String(p?.esqModalName || '').trim()) return 'ESQ';
    }
    return 'LSP/QD';
  };

  const [isSsoLoggingIn, setIsSsoLoggingIn] = React.useState(() => {
    const { ssoToken } = getSharedParams();
    return Boolean(ssoToken);
  });

  const [showLanding, setShowLanding] = React.useState(false);
  const [isResolvingSharedMode, setIsResolvingSharedMode] = React.useState(() => {
    const { token, sid, loadId } = getSharedParams();
    return Boolean(token || sid || loadId);
  });

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const { token, sid, loadId, ssoToken } = getSharedParams();

      if (ssoToken) {
        try {
          const res = await fetch(buildApiUrl('/api/auth/sso-verify'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ token: ssoToken })
          });
          const data = await res.json().catch(() => ({}));
          if (res.ok && data?.ok) {
            localStorage.setItem('auth_token', data.token);
            localStorage.setItem('auth_user', JSON.stringify(data.user));
            setAuthedUser(data.user);
            // Default to LSP/QD if not already selected
            if (!sessionStorage.getItem('design_tool.mode_selected')) {
              sessionStorage.setItem('design_tool.mode_selected', 'true');
              sessionStorage.setItem('design_tool.page_mode', 'LSP/QD');
              localStorage.setItem('design_tool.mode', 'LSP/QD');
            }
          }
        } catch (e) {
          console.error('SSO auto-login failed:', e);
        } finally {
          // Remove sso_token from URL
          try {
            const clean = new URL(window.location.href);
            clean.searchParams.delete('sso_token');
            window.history.replaceState({}, '', clean.toString());
          } catch { }
          if (!cancelled) {
            setIsSsoLoggingIn(false);
          }
        }
      }

      if (!token && !sid && !loadId) {
        setIsResolvingSharedMode(false);
        return;
      }

      try {
        let payload: any = null;
        if (token) {
          payload = await decryptSharedDesignFromUrlParam(token);
        } else if (sid) {
          const res = await fetch(buildApiUrl(`/api/share/${encodeURIComponent(String(sid))}`), {
            headers: { Accept: 'application/json' }
          });
          const j = await res.json().catch(() => ({}));
          if (res.ok && j?.ok) payload = j?.payload;
        } else if (loadId) {
          const res = await fetch(buildApiUrl(`/api/designs/${loadId}`), {
            headers: { ...authHeaders() }
          });
          const j = await res.json().catch(() => ({}));
          if (res.ok && j?.ok) {
            payload = j?.design;
            // Parse design_json if it's a string
            if (payload && typeof payload.design_json === 'string') {
              try { payload = JSON.parse(payload.design_json); } catch { }
            }
          }
        }

        const mode = inferModeFromSharedPayload(payload);
        try {
          sessionStorage.setItem('design_tool.mode_selected', 'true');
          sessionStorage.setItem('design_tool.page_mode', mode);
          localStorage.setItem('design_tool.mode', mode);
        } catch { }
      } catch {
        // Keep default mode fallback if payload cannot be read.
      } finally {
        if (!cancelled) {
          setShowLanding(false);
          setIsResolvingSharedMode(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, []);

  if (isSsoLoggingIn) {
    return <div style={{ height: '100vh', display: 'grid', placeItems: 'center', background: '#f8fafc', color: '#64748b', fontWeight: 600 }}>Logging in...</div>;
  }

  if (isResolvingSharedMode) {
    return <div style={{ height: '100vh', display: 'grid', placeItems: 'center', background: '#f8fafc', color: '#64748b', fontWeight: 600 }}>Loading design...</div>;
  }

  if (showLanding) {
    return (
      <LandingPage
        onSelect={(mode) => {
          try {
            sessionStorage.setItem('design_tool.mode_selected', 'true');
            sessionStorage.setItem('design_tool.page_mode', mode);
            localStorage.setItem('design_tool.mode', mode);
          } catch { }
          setShowLanding(false);
        }}
      />
    );
  }

  return <App />;
};

root.render(
  <React.StrictMode>
    <ToastHost />
    {isPromoRoute ? (
      <React.Suspense fallback={<div className="flex items-center justify-center h-screen">Loading Promo Price List...</div>}>
        <PromoPricePage />
      </React.Suspense>
    ) : isLspQdGuideRoute ? (
      <React.Suspense fallback={<div className="flex items-center justify-center h-screen">Loading LSP & QD User Guide...</div>}>
        <LspQdUserGuidePage />
      </React.Suspense>
    ) : isEsqGuideRoute ? (
      <React.Suspense fallback={<div className="flex items-center justify-center h-screen">Loading ESQ User Guide...</div>}>
        <EsqUserGuidePage />
      </React.Suspense>
    ) : isAdminRoute ? (
      <React.Suspense fallback={<div className="flex items-center justify-center h-screen">Loading Admin...</div>}>
        <AdminApp />
      </React.Suspense>
    ) : isCategoriesRoute ? (
      <DesignProvider>
        <LandingPage
          onSelect={(mode) => {
            try {
              sessionStorage.setItem('design_tool.mode_selected', 'true');
              sessionStorage.setItem('design_tool.page_mode', mode);
              localStorage.setItem('design_tool.mode', mode);
            } catch { }
            window.location.href = '/design';
          }}
        />
      </DesignProvider>
    ) : (
      <>
        {isDesignRoute ? (
          <DesignProvider>
            <GlobalLoader />
            <DesignModeWrapper />
          </DesignProvider>
        ) : (
          <React.Suspense fallback={<div className="flex items-center justify-center h-screen">Loading Dashboard...</div>}>
            <CustomerDashboardApp />
          </React.Suspense>
        )}
      </>
    )}
  </React.StrictMode>
);